<script setup>
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';

import Button from 'next/button/Button.vue';
import DeleteAttributeMapDialog from './DeleteAttributeDialog.vue';
import EditAttributeDialog from './EditAttributeDialog.vue';

const props = defineProps({
  samlProvider: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const attributeOptions = computed(() => {
  const attributesMap = props.samlProvider.config.attributes || {};
  return Object.entries(attributesMap).map(([key, value]) => ({
    key,
    value:
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(
        `WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.FORM.VALUE.OPTIONS.${value.toUpperCase()}`
      ) || value,
  }));
});

const deleteAttributeMapDialog = ref(null);
const editAttributeDialog = ref(null);

const onEdit = attributeKey => {
  editAttributeDialog.value.open(attributeKey, props.samlProvider);
};

const onDelete = attributeKey => {
  deleteAttributeMapDialog.value.open(attributeKey, props.samlProvider);
};
</script>

<template>
  <table class="table w-full table-striped woot-table">
    <template v-if="attributeOptions.length">
      <thead class="thead-dark">
        <tr>
          <th scope="col">
            <div
              class="text-start text-sm font-medium normal-case text-ash-900 whitespace-nowrap tracking-normal"
            >
              {{ t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.TABLE.HEADER.KEY') }}
            </div>
          </th>
          <th scope="col">
            <div
              class="text-start text-sm font-medium normal-case text-ash-900 whitespace-nowrap tracking-normal"
            >
              {{ t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.TABLE.HEADER.VALUE') }}
            </div>
          </th>
        </tr>
      </thead>

      <tr
        v-for="attributeOption in attributeOptions"
        :key="attributeOption.key"
        class="bg-n-solid-1"
      >
        <td class="attribute-field">
          {{ attributeOption.key }}
        </td>
        <td class="attribute-field">
          {{ attributeOption.value }}
        </td>
        <td class="attribute-field">
          <div class="flex justify-end gap-1">
            <Button
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="onEdit(attributeOption.key)"
            />
            <Button
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="onDelete(attributeOption.key)"
            />
          </div>
        </td>
      </tr>
    </template>
    <tbody v-else>
      <tr>
        <td colspan="3" class="p-8 text-center text-n-slate-5 text-sm">
          {{ t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.TABLE.EMPTY') }}
        </td>
      </tr>
    </tbody>
  </table>

  <DeleteAttributeMapDialog ref="deleteAttributeMapDialog" />
  <EditAttributeDialog ref="editAttributeDialog" />
</template>

<style scoped lang="scss">
th {
  @apply px-2 pb-4;
}

.attribute-field {
  @apply px-2 py-4 bg-n-slate-2;
}
</style>
